// Permission name registry — values match the canonical <subject>.<action>
// strings stored in the permissions table. Keep this file in sync with
// db/seeds/006_permissions.rb (the source of truth); parity is enforced by
// tests/policies/permissionsSeedParity.spec.ts.
//
// The shape is unchanged from the legacy version (admin.* nesting) so that
// every consuming Vue component / router / composable continues to work
// without further edits. Only the string literals were updated.

export const PERMISSIONS = {
  admin: {
    dashboard: 'admin_dashboard.access',

    resources: {
      access: 'file_resource.access',
      create: 'file_resource.create',
      update: 'file_resource.update',
      destroy: 'file_resource.delete',
      promote: 'file_resource.promote',
      replaceFile: 'file_resource.replace'
    },
    settings: {
      manage: 'application_setting.update'
    },
    systemMonitoring: {
      show: 'system_monitoring.access'
    },
    // Rails console engines. The System Monitoring dashboard receives a
    // server-filtered tool list (SystemMonitoringController#available_tool_objects),
    // so these have no frontend consumer today — registry-only for seed parity.
    console: {
      audits1984: 'audits1984.access',
      goodJob: 'good_job.access',
      letterOpener: 'letter_opener.access',
      pghero: 'pghero.access'
    },
    categories: {
      view: 'category.view',
      create: 'category.create',
      update: 'category.update',
      delete: 'category.delete',
      removeClub: 'category.remove_club',
      manageClubs: 'category.manage_clubs'
    },
    termAcknowledgementForms: {
      view: 'term_acknowledgement_form.view',
      manage: 'term_acknowledgement_form.manage'
    },
    announcements: {
      view: 'announcement.view',
      create: 'announcement.create',
      update: 'announcement.update',
      delete: 'announcement.delete',
      publish: 'announcement.publish',
    },
    users: {
      viewUsers: 'user.view',
      importUsers: 'user.import',
      createUsers: 'user.create',
      viewComments: 'user_comment.view',
      addComment: 'user_comment.create',
    },
    faqs: {
      view: 'faq.view',
      create: 'faq.create',
      update: 'faq.update',
      delete: 'faq.delete',
    },
    emailTemplates: {
      access: 'email_template.access',
      view: 'email_template.view',
      create: 'email_template.create',
      update: 'email_template.update',
      delete: 'email_template.delete',
      archive: 'email_template.archive',
      managePermissions: 'email_template.manage_permissions',
      testSend: 'email_template.test_send',
      restore: 'email_template.restore',
      manageAll: 'email_template.manage_all'
    },
    eventTypes: {
      view: 'event_type.view',
      create: 'event_type.create',
      update: 'event_type.update',
      delete: 'event_type.delete',
    },
    eventForms: {
      access: 'event_form.access',
      viewArchived: 'event_form.view_archived',
    },
    clubApplications: {
      access: 'club_application.access',
      assignApprovers: 'club_application.assign_approvers',
    },
    terms: {
      view: 'term.view',
      update: 'term.update',
      exportExecutives: 'term.export_executives',
    },
    riskManagement: {
      access: 'risk_management.access',
    },
    riskLevels: {
      view: 'risk_level.view',
      create: 'risk_level.create',
      update: 'risk_level.update',
    },
    riskScores: {
      view: 'risk_score.view',
      edit: 'risk_score.update',
    },
    avEquipmentCategories: {
      view: 'av_equipment_category.view',
      create: 'av_equipment_category.create',
      edit: 'av_equipment_category.update',
    },
    avEquipmentItems: {
      view: 'av_equipment_item.view',
      create: 'av_equipment_item.create',
      edit: 'av_equipment_item.update',
    },
    organizations: {
      view: 'organization.view',
      updateLogo: 'organization.update_logo',
    },
    clubCodes: {
      manage: 'club_code.update',
    },
    clubCodeReports: {
      access: 'club_code_report.access',
    },
    clubsInactiveReports: {
      access: 'clubs_inactive_report.access',
    },
    clubEmails: {
      view:   'club_email.view',
      manage: 'club_email.update',
    },
    finances: {
      view: 'finance.view',
    },
    locations: {
      manage: 'bookable_location.update',
    },
    clubs: {
      view:            'club.view',
      viewPurged:      'club.view_purged',
      rename:          'club.rename',
      viewComments:    'club_comment.view',
      createComment:   'club_comment.create',
      viewAttachments: 'club_attachment.view',
      attachFiles:     'club_attachment.create',
      updateProfile:   'club_profile.update',
      assignContact:   'club_contact.assign'
    },
    clubProfileRequests: {
      access: 'club_profile_request.access',
    },
    clubMembers: {
      view: 'club_member.view',
      create: 'club_member.create',
      replaceExecutive: 'club_member.replace_executive',
      export: 'club_member.export',
      remove: 'club_member.remove',
    },
    contactLists: {
      manage: 'contact_list.manage',
    },
    associatedClubs: {
      view: 'associated_club.view',
      manage: 'associated_club.update',
    },
    groups: {
      view: 'group.view',
      create: 'group.create',
      update: 'group.update',
      delete: 'group.delete',
      updatePermissions: 'group.update_permissions',
      manageMembers: 'group.manage_members',
      // groups.root permission was dropped — Root group membership is now
      // checked via user.groups.exists?(name: "Root") in backend code.
    },
    reimbursementRequests: {
      access: 'reimbursement_request.access',
      batchApprove: 'reimbursement_request.batch_approve',
      batchDeliver: 'reimbursement_request.batch_deliver',
      importToBc: 'reimbursement_request.import_to_bc',
      viewBatches: 'reimbursement_request.view_batches',
      generateDocuments: 'reimbursement_request.generate_documents',
      manageBatch: 'reimbursement_request.manage_batch',
    },
    forms: {
      access: 'form.access',
      managePermissions: 'form.manage_permissions',
      manageAvailability: 'form_schedule.update',
    },
    emailLogs: {
      access: 'email_log.access',
      delete: 'email_log.delete',
    }
  }
} as const

// Recursively collect the string-literal leaves of the registry into a closed
// union. This makes `<Can do="...">` and `store.can(...)` typo-proof: a value
// that isn't one of the canonical permission strings fails to compile.
type Leaves<T> = T extends string ? T : { [K in keyof T]: Leaves<T[K]> }[keyof T]

/**
 * Closed union of every permission string in PERMISSIONS, e.g.
 * 'admin_dashboard.access' | 'category.view' | 'group.update' | ...
 */
export type Permission = Leaves<typeof PERMISSIONS>
